import Box from '@material-ui/core/Box';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';
import { StoreCategory, UIBlockElement } from 'common-ts/dist/models/Product';
import React from 'react';
import { useAppSelector } from '../reduxhooks';
import { CategoryPageContext, CategoryPageContextValue } from './CategoryPageContext';
import { BlockProductArrayCenterData, RenderBlockElement } from './PageComponentElementBlocks';
import { SYSTEM_ALL_HANDLE } from '../terbebasApi';

const useStyles = makeStyles((theme: Theme) =>
createStyles({
  productListBody: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',

    marginTop: '40px', // This is same size as category bar
    [theme.breakpoints.up('sm')]: {
      marginTop: '60px',
    },
  },
}),
);

interface Props {
  categoryName: string
}

// Default scaffold for a category without explicit elements: one
// BlockCollectionProducts, mode resolved by the block (store default).
function defaultScaffold(): UIBlockElement[] {
  return [{
    elementDesktopCode: 'BlockCollectionProducts',
    elementDesktopContentJson: {},
    elementMobileCode: 'BlockCollectionProducts',
    elementMobileContentJson: {},
  }];
}

// Category page (Kategori) renderer, also serving the /shop root feed.
// The page is a UIBlockElement[] scaffold — same vocabulary as custom pages —
// rendered inside a CategoryPageContext that tells BlockCollectionProducts
// which Koleksi are in scope. Pagination lives inside that block, so custom
// scaffold blocks (heroes, copy) render eagerly.
export default function PageProductList({ categoryName }: Props) {
  const classes = useStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  const productDatabase = useAppSelector(state => state.productsList);
  const categoriesDictionary = productDatabase.categoriesDictionary ?? {};

  // /shop (and the legacy landing/start campaign routes) resolve through the
  // system "Semua Produk" Kategori, so they render byte-identically to
  // /shop/category/semua-produk — same Koleksi, same layout, same mode.
  // Previously the root had its own hard-coded discovery block, which silently
  // diverged: it showed ONLY curated Koleksi (nothing at all once a merchant
  // gave one a layout) and ignored the store's categoryDefaultMode.
  const isRootShop = !categoryName || categoryName === 'landing' || categoryName.startsWith('start');
  const category: StoreCategory | undefined =
    categoriesDictionary[isRootShop ? SYSTEM_ALL_HANDLE : categoryName];

  let contextValue: CategoryPageContextValue;
  let scaffold: UIBlockElement[];

  if (category) {
    contextValue = {
      categoryId: category.categoryId,
      collectionHandles: category.collectionHandles,
    };
    scaffold = category.elements.length ? category.elements : defaultScaffold();
  } else if (isRootShop) {
    // FALLBACK ONLY: stores created before the system "Semua Produk" Kategori
    // existed have no such category to resolve, so the root keeps its legacy
    // discovery feed over the merchant's curated Koleksi (or the whole-catalog
    // grid). Stores with the system Kategori never reach this branch.
    contextValue = {
      categoryId: '',
      collectionHandles: productDatabase.discoveryCollectionsOrder,
    };
    scaffold = [{
      elementDesktopCode: 'BlockCollectionProducts',
      elementDesktopContentJson: { mode: 'discovery' },
      elementMobileCode: 'BlockCollectionProducts',
      elementMobileContentJson: { mode: 'discovery' },
    }];
  } else if (productDatabase.collectionsDictionary[categoryName]) {
    // Legacy /shop/category/<koleksi-handle> links: treat the Koleksi as a
    // one-Koleksi category.
    contextValue = { categoryId: categoryName, collectionHandles: [categoryName] };
    scaffold = defaultScaffold();
  } else {
    // Unknown handle: fall back to products whose legacy category
    // (productType) matches, as a flat grid.
    const productIds = productDatabase.efficientProductsOrder.filter((productId) =>
      productDatabase.productsDictionary[productId]?.category === categoryName);
    const param: BlockProductArrayCenterData = {
      productIds,

      containerWidthXs: '100%',
      containerWidthSm: '81%',
      containerWidthMd: '81%',
      containerWidthLg: '81%',
      productWidthXs: '48%',
      productWidthSm: '30%',
      productWidthMd: '30%',
      productWidthLg: '30%',

      containerMargin: isDesktop ? '85px 0px 60px 0px' : '65px 0px 60px 0px',
      productPadding: isDesktop ? '20px 0px 9px 0px' : '8px 0px 9px 0px',
    };
    contextValue = { categoryId: categoryName, collectionHandles: [] };
    scaffold = [{
      elementDesktopCode: 'BlockProductArrayCenter',
      elementDesktopContentJson: param,
      elementMobileCode: 'BlockProductArrayCenter',
      elementMobileContentJson: param,
    }];
  }

  return (
    <CategoryPageContext.Provider value={contextValue}>
      <Box className={classes.productListBody} id={'scrollableDiv'}>
        {
          scaffold.map((block, i) => (
            <RenderBlockElement
              key={contextValue.categoryId + '-' + i}
              code={isDesktop ? block.elementDesktopCode : block.elementMobileCode}
              contentJson={isDesktop ? block.elementDesktopContentJson : block.elementMobileContentJson}
            />
          ))
        }
      </Box>
    </CategoryPageContext.Provider>
  );
}
